/**
 * 엑셀에서 복사한 것을 항목 줄로.
 *
 * 인테리어 견적은 대부분 엑셀에서 출발한다. 「이미 있는 표를 옮겨 적는 일」 이
 * 새 견적의 절반이라, 붙여넣기가 되면 손이 크게 준다.
 *
 * 엑셀은 셀을 **탭**으로, 줄을 개행으로 준다. 셀 안에 개행이 있으면 따옴표로
 * 감싸는데, 그 경우는 받지 않는다 — 견적 표에 여러 줄짜리 셀은 거의 없고,
 * 따옴표까지 다루면 「왜 이건 되고 저건 안 되나」 가 늘어난다.
 */

import { evalQtyExpr, isExpr } from "../qty-expr";
import { MONEY_MAX, QTY_MAX } from "../schema";
import { applyAmountColumns, rowFieldsOf } from "../import/apply-columns";
import { guessColumns, inferAmountColumn, normLabel } from "../import/detect";
import { ColumnMap } from "../import/types";
import { parseMoney, parseNumber } from "./cell-number";
import { splitSimplePrice } from "./reducer";
import { UNIT_SUGGESTIONS } from "../../template/trades";
import {
  EditorItemPatch,
  FilledItemInput,
} from "./types";

/** 붙여넣기가 기대하는 열 순서. 화면이 이 문구를 그대로 안내한다. */
export const PASTE_COLUMNS = [
  "작업내역",
  "규격",
  "수량",
  "자재 단가",
  "노무 단가",
  "비고",
] as const;

export interface PasteResult {
  rows: FilledItemInput[];
  /** 작업내역이 비어 통째로 버린 줄 수. */
  skipped: number;
  /**
   * 담기는 했지만 **숫자 칸을 못 읽은** 줄 수.
   *
   * 0 으로 눌러 놓고 조용히 넘어가면 「1줄 담았습니다」 를 보고 넘어갔다가
   * 견적서에 0원짜리 줄이 남는다. 세어서 「확인해 주세요」 로 말한다.
   */
  unclear: number;
}

export { parseMoney, parseNumber } from "./cell-number";

/* ── 칸 하나 · 줄 하나 읽기 — 붙여넣기·시트·(나중에) 엑셀 가져오기가 이 한 벌을 쓴다 ── */

/** 칸이 채울 곳. `simple_price` 는 간단 모드의 단가 한 칸(자재+노무 합)이다. */
export type RowField =
  | "space_code"
  | "scope"
  | "item"
  | "unit"
  | "qty"
  | "material_unit_price"
  | "labor_unit_price"
  | "simple_price"
  | "cost_unit_price"
  | "note";

export interface CellParseContext {
  /** 수량식을 받을지. 받지 않으면 단순 숫자만 읽는다. */
  qtyExpr: boolean;
  /** 수량식의 `평`. */
  area_pyeong: number | null;
  /** 수량식의 `m²`. 없으면 평×3.305785. */
  area_m2?: number | null;
  /** 주면 공간 칸을 이름·코드로 맞춘다. 안 주면 적힌 값을 코드로 쓴다. */
  spaces?: { code: string; label: string }[];
}

/** 칸 하나를 읽은 결과. 못 읽으면 `{ ok: false }` — 0 으로 누르지 않는다. */
export type CellParse = { ok: true; patch: EditorItemPatch } | { ok: false };

// 수량 칸의 「숫자+단위」(「3EA」) — 수량식·순수 숫자로 못 읽을 때만 본다. 단위는 그 행 단위 칸이 비었을 때만 채운다.
const QTY_WITH_UNIT =
  /^(\d[\d,]*(?:\.\d+)?|\.\d+)\s*([A-Za-z가-힣\u3380-\u33ff²³][A-Za-z가-힣\u3380-\u33ff²³0-9]{0,7})$/;

const qtyWithUnit = (raw: string): { qty: number; unit?: string } | null => {
  // 「1,000원」·「₩3」 은 통화 표기일 뿐이라 붙여넣기(parseNumber)와 같게 숫자만 읽는다.
  const plain = parseNumber(raw);
  if (plain !== null) return plain > QTY_MAX ? null : { qty: plain };
  const m = QTY_WITH_UNIT.exec(raw.trim());
  if (!m) return null;
  // 「3x」 는 치다 만 곱셈, 「1e3」·「3e」 는 지수 표기(오타), 「원…」 은 통화지 단위가 아니다.
  if (/^[xXeE]$/.test(m[2]) || /^[eE]\d/.test(m[2]) || m[2].startsWith("원")) return null;
  const qty = parseNumber(m[1]);
  if (qty === null || qty > QTY_MAX) return null;
  const known = UNIT_SUGGESTIONS.find((u) => u.toLowerCase() === m[2].toLowerCase());
  return { qty, unit: known ?? m[2] };
};

const TEXT_FIELDS = new Set<RowField>(["scope", "item", "unit", "note"]);

/** 칸 하나를 읽어 행 패치로. 못 읽으면 `{ ok: false }` — 0 으로 누르지 않는다. */
export const parseCell = (
  field: RowField,
  raw: string,
  ctx: CellParseContext,
  current: { labor_unit_price: number; unit?: string } = { labor_unit_price: 0 }
): CellParse => {
  const text = raw.trim();
  if (TEXT_FIELDS.has(field)) return { ok: true, patch: { [field]: text } };

  if (field === "space_code") {
    if (text === "" || !ctx.spaces) return { ok: true, patch: { space_code: text } };
    const hit = ctx.spaces.find((s) => s.label === text || s.code === text);
    return hit ? { ok: true, patch: { space_code: hit.code } } : { ok: false };
  }

  if (field === "qty") {
    if (ctx.qtyExpr && isExpr(text)) {
      const result = evalQtyExpr(text, { area_pyeong: ctx.area_pyeong, area_m2: ctx.area_m2 });
      if (result.ok) return { ok: true, patch: { qty: result.value, qty_expr: text } };
    } else {
      const qty = parseNumber(text);
      // 식을 지우는 일은 `applyItemPatch` 가 한다(수량만 오면 식을 버린다). 상한을 넘기면 저장에서야 막히니 여기서 못 읽은 것으로 본다.
      if (qty !== null) return qty > QTY_MAX ? { ok: false } : { ok: true, patch: { qty } };
    }
    const withUnit = qtyWithUnit(text);
    if (!withUnit) return { ok: false };
    const hasUnit = (current.unit ?? "").trim() !== "";
    // 단위가 이미 있는 행에 「3EA」 를 치면 기존 단위를 유지하고 수량만 바꾼다(의도).
    if (hasUnit || withUnit.unit === undefined) return { ok: true, patch: { qty: withUnit.qty } };
    return { ok: true, patch: { qty: withUnit.qty, unit: withUnit.unit } };
  }

  // 원가는 빈 칸이 「미입력」(null)이다 — 0 은 원가 0원이라 다르다.
  if (field === "cost_unit_price" && text === "") return { ok: true, patch: { cost_unit_price: null } };
  const money = parseMoney(text);
  if (money === null || money > MONEY_MAX) return { ok: false };
  if (field === "simple_price") {
    return { ok: true, patch: splitSimplePrice(current, money) };
  }
  return { ok: true, patch: { [field]: money } };
};

export const blankFilledItem = (): FilledItemInput => ({
  space_code: "",
  scope: "",
  item: "",
  unit: "",
  qty: 0,
  material_unit_price: 0,
  labor_unit_price: 0,
  note: "",
});

/** 칸 배열을 새 항목 한 줄로. `fields[i]` 가 null 이면 그 칸은 버린다. 못 읽은 칸 수를 같이 준다. */
export const rowFromCells = (
  cells: string[],
  fields: (RowField | null)[],
  ctx: CellParseContext
): { row: FilledItemInput; unclear: number } => {
  let row = blankFilledItem();
  let unclear = 0;
  let unitHint = "";
  fields.forEach((field, i) => {
    if (field === null) return;
    const parsed = parseCell(field, cells[i] ?? "", ctx);
    if (!parsed.ok) {
      unclear += 1;
    } else if (field === "qty" && parsed.patch.unit !== undefined) {
      // 수량 칸에서 읽힌 단위는 단위 열이 빈 채로 끝날 때만 쓴다(열 순서와 무관하게).
      const { unit, ...rest } = parsed.patch;
      row = { ...row, ...rest };
      unitHint = unit;
    } else {
      row = { ...row, ...parsed.patch };
    }
  });
  if (unitHint !== "" && row.unit === "") row = { ...row, unit: unitHint };
  // 빈 원가 칸은 칸을 아예 두지 않는다 — 새 줄에선 없음과 null 이 같은 「미입력」이다.
  if (row.cost_unit_price === null) {
    const { cost_unit_price: _none, ...rest } = row;
    row = rest;
  }
  return { row, unclear };
};

// 붙여넣기 열 순서(PASTE_COLUMNS)를 칸 이름으로.
const PASTE_FIELDS: ColumnMap = [
  "item",
  "unit",
  "qty",
  "material_unit_price",
  "labor_unit_price",
  "note",
];

export { isCostLabel } from "../import/detect";

// 가져오기가 모르는 맨 「자재」「노무」 열 — 붙여넣기는 예전처럼 단가로 담는다.
const BARE_PRICE: Record<string, "material_unit_price" | "labor_unit_price"> = {
  자재: "material_unit_price",
  재료: "material_unit_price",
  노무: "labor_unit_price",
  인건: "labor_unit_price",
};

// 담지 않는 열(대조용 금액·공종) — 머리글다운지 셀 때 빼고 센다.
const NOT_FILLED = new Set(["amount_total", "amount_part", "trade"]);

// 머리글 줄이 있으면 엑셀 가져오기와 같은 규칙(`guessColumns`·`inferAmountColumn`)으로 열을 맞춘다 · 작업내역 말고 아무것도 못 알아보면 순서대로.
const pasteColumnsOf = (header: string[], data: string[][]): ColumnMap => {
  const guessed = guessColumns(header);
  const withBare: ColumnMap = guessed.map((f, i) => {
    const bare = f === null ? BARE_PRICE[headerName(header[i] ?? "")] : undefined;
    return bare !== undefined && !guessed.includes(bare) ? bare : f;
  });
  // 붙여넣기엔 공간 목록이 없어 공간 열은 버린다.
  const columns = inferAmountColumn(data, withBare, 0).map((f) => (f === "space_code" ? null : f));
  // 0번 열이 비었을 때만 작업내역으로 · 이미 매핑돼 있으면 매핑 안 된 첫 텍스트 열을 쓴다.
  if (!columns.includes("item")) {
    const textAt = columns.findIndex(
      (f, i) => f === null && data.some((cells) => (cells[i] ?? "").trim() !== "" && parseNumber(cells[i].trim()) === null),
    );
    if (columns[0] === null) columns[0] = "item";
    else if (textAt >= 0) columns[textAt] = "item";
  }
  const filled = new Set(columns.filter((f) => f !== null && !NOT_FILLED.has(f)));
  return filled.size > 1 ? columns : PASTE_FIELDS;
};

// 칸 전체가 이 이름(괄호 속 글자·공백·구두점을 걷은 것)일 때만 「아는 열 이름」 으로 센다 — 「철거 시공비」 같은 자료를 머리글로 오판하지 않게.
const HEADER_NAMES = new Set([
  "작업내역", "품명", "항목", "내역", "내용", "품목", "품목명", "자재명", "작업명", "공사명", "명칭",
  "공종", "공정", "공종명", "공정명", "대분류", "중분류", "구분", "공간", "실", "실명", "부위", "위치", "범위", "세부",
  "규격", "단위", "수량",
  "단가", "자재", "재료", "노무", "인건", "자재단가", "재료단가", "자재비단가", "재료비단가", "노무단가", "노무비단가",
  "인건비단가", "노임단가", "경비", "경비단가", "판매단가", "공급단가", "원가", "원가단가",
  "자재비", "재료비", "노무비", "인건비", "노임", "시공비", "금액", "합계", "자재금액", "재료금액", "노무금액",
  "인건비금액", "공급가액", "공급가", "총액", "소계",
  "비고", "메모", "참고", "특기사항", "no", "번호", "순번", "연번",
]);

const headerName = (cell: string): string => normLabel(cell.replace(/\([^)]*\)|\[[^\]]*\]/g, ""));

/**
 * 머리글로 보이는 줄인가. 엑셀에서 표를 통째로 복사하면 첫 줄이 딸려 온다.
 *
 * 이름만 보면 안 된다 — 작업내역이 그냥 「항목」 인 자료 줄이 조용히 사라진다.
 * 같이 본다: **첫 줄이면서**, 첫 칸이 머리글 이름이거나 **아는 열 이름이 2칸 이상**이고, **숫자 칸이 하나도 안 읽히는** 줄.
 *
 * 머리글은 붙여넣은 표의 맨 위에만 있다. 가운데에 「항목」 이라고만 적힌 줄이
 * 나오면 그것은 자료다 — 둘째 줄부터는 이름이 뭐든 자료로 본다.
 */
// 첫 칸이 이 이름 그대로면 머리글 후보 — 「욕실 내역 정리」 같은 자료 첫 줄을 막으려 글자 포함이 아니라 일치로 본다.
const HEADER_FIRST = new Set(["작업내역", "품명", "항목", "내역", "품목", "품목명", "자재명", "작업명", "공사명", "공종", "공정"]);

const looksLikeHeader = (cells: string[]): boolean => {
  const first = cells[0]?.trim() ?? "";
  const named = HEADER_FIRST.has(first.replace(/\s/g, ""));
  const known = cells.filter((c) => c.trim() !== "" && HEADER_NAMES.has(headerName(c))).length;
  if (!named && known < 2) return false;

  // 숫자로 읽히는 칸이 하나라도 있으면 자료 줄이다.
  return cells.every((c) => {
    const cell = c.trim();
    return cell === "" || parseNumber(cell) === null;
  });
};

/**
 * 붙여넣은 텍스트를 항목 줄로. 읽을 수 없는 줄은 **버리고 세어서** 알린다 —
 * 조용히 빼면 몇 줄이 사라졌는지 알 수가 없다.
 *
 * 규칙: 탭이 없으면 그 줄 전체를 작업내역으로 본다(한 열만 복사한 경우).
 * 작업내역이 비면 그 줄은 버린다 — 이름 없는 항목은 견적서에서 빈 줄이다.
 */
export const parsePastedRows = (raw: string): PasteResult => {
  const lines = raw
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((line) => line.trim() !== "")
    .map((line) => line.split("\t"));
  const rows: FilledItemInput[] = [];
  let skipped = 0;
  let unclear = 0;

  // 머리글은 맨 위에만 있다 — 첫 줄을 지나면 이름이 뭐든 자료로 본다.
  const header = lines.length > 0 && looksLikeHeader(lines[0]);
  const data = header ? lines.slice(1) : lines;
  const columns: ColumnMap = header ? pasteColumnsOf(lines[0], data) : PASTE_FIELDS;
  const fields = rowFieldsOf(columns);

  for (const cells of data) {
    const item = (cells[Math.max(fields.indexOf("item"), 0)] ?? "").trim();
    if (item === "") {
      skipped += 1;
      continue;
    }

    const built = rowFromCells(cells, fields, {
      qtyExpr: false,
      area_pyeong: null,
    });
    const applied = applyAmountColumns(built.row, cells, columns);
    if (built.unclear + applied.unclear > 0) unclear += 1;
    rows.push(applied.row);
  }

  return { rows, skipped, unclear };
};
